import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Lock, Crown, Check, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Paywall = () => {
  const navigate = useNavigate();
  const { checkSubscription, hasSubscription } = useAuth();
  const [checking, setChecking] = useState(false);
  const [accessError, setAccessError] = useState('');

  // Auto-verify on mount — redirects silently if coupon was just applied
  useEffect(() => {
    (async () => {
      try {
        const result = await checkSubscription();
        if (result?.has_access) {
          window.location.href = '/dashboard';
        }
      } catch {}
    })();
  }, []);

  const handleCheckAccess = async () => {
    setChecking(true);
    setAccessError('');
    try {
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 10000)
      );
      const result = await Promise.race([checkSubscription(), timeout]);
      if (result.has_access) {
        window.location.href = '/dashboard';
      } else if (result.reason === 'not_authenticated' || result.reason === 'error') {
        setAccessError('Sessão expirada. Faça login novamente.');
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      } else {
        setAccessError('Plano não encontrado. Peça ao administrador para atribuir o plano Teste e tente novamente.');
      }
    } catch {
      setAccessError('Não foi possível verificar o acesso. Tente novamente.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-lg w-full p-8 text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-purple-100 rounded-full mb-6">
          <Lock className="w-10 h-10 text-purple-600" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
          Acesso Exclusivo para Assinantes
        </h1>

        <p className="text-gray-600 mb-8">
          Para acessar todo o conteúdo da plataforma Simby e desenvolver sua inteligência relacional,
          você precisa ser assinante.
        </p>

        <div className="bg-purple-50 rounded-lg p-6 mb-6 text-left">
          <div className="flex items-center space-x-2 mb-4">
            <Crown className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">O que você terá acesso:</h3>
          </div>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              Todas as ilhas e lições de Inteligência Relacional
            </li>
            <li className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              Simuladores de conversas com IA
            </li>
            <li className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              Diário de emoções com insights
            </li>
            <li className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              Acompanhamento de progresso
            </li>
            <li className="flex items-center">
              <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              Empatia de Emergência
            </li>
          </ul>
        </div>

        <div className="space-y-3">
          <Button
            onClick={() => navigate('/subscription/plans')}
            size="lg"
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            Ver planos e assinar
          </Button>

          <Button
            onClick={handleCheckAccess}
            variant="outline"
            className="w-full"
            disabled={checking}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Verificando...' : 'Já tenho acesso — verificar'}
          </Button>
          {accessError && (
            <p className="text-sm text-red-600 text-center">{accessError}</p>
          )}

          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="w-full"
          >
            Voltar para o início
          </Button>
        </div>

        <p className="text-sm text-gray-500 mt-6">
          A partir de R$ 20,75/mês no plano anual
        </p>
      </Card>
    </div>
  );
};

export default Paywall;

